<template>
  <div
    ref="carouselContainer"
    :class="['carousel-container', theme, responsiveClasses, {'scrolling': isScrolling}]">
    <!-- This div below is needed to hide the native scrollbar -->
    <div>
      <div
        ref="scrollContainer"
        class="scroll-container"
        :style="{ height: showScrollBar ? hidingNativeScrollbarHeight : 'auto' }"
        @scroll="onScrollEvent">
        <div ref="itemsContainerWithOffset">
          <ul :class="['items-container', { 'spaced': withMargins }]">
            <slot />
          </ul>
        </div>
      </div>
    </div>
    <div v-show="isScrollableCarousel && showScrollBar">
      <div class="scroll-gripper-container">
        <div
          ref="scrollGripper"
          class="scroll-gripper"
          :style="{transform: 'translateX(' + scrollGripperXPosition + 'px )'}"
          @mousedown="onMouseDownEventScrollGripper" />
      </div>
      <div v-if="showArrows">
        <div
          v-if="!isLeftmost"
          class="bound left">
          <button
            class="arrow left-arrow"
            aria-label="Voir plus de titres"
            @click="clickArrow(-1)">
            <span class="icon-arrow-previous" />
          </button>
        </div>
        <div
          v-if="!isRightmost"
          class="bound right">
          <button
            class="arrow right-arrow"
            aria-label="Voir plus de titres"
            @click="clickArrow(1)">
            <span class="icon-arrow-next" />
          </button>
        </div>
      </div>
      <div v-else>
        <div
          v-if="!isLeftmost"
          class="bound left"
          @mouseover="startAutoScrolling(-1)"
          @mouseout="stopAutoScrolling" />
        <div
          v-if="!isRightmost"
          class="bound right"
          @mouseover="startAutoScrolling(1)"
          @mouseout="stopAutoScrolling" />
      </div>
    </div>
  </div>
</template>

<script>
import Animation from './Animation'
const themes = ['dark', 'light', 'dark-img']

export default {
  name: 'WptvCarousel',
  props: {
    /**
     * The theme wanted ('dark' || 'light')
     */
    theme: {
      type: String,
      default: 'light',
      validator: (value) => themes.includes(value)
    },
    /**
     * The number of pixel to scroll when autoscroll is enabled with mouse over boundaries
     */
    step: {
      type: Number,
      default: 10
    },
    /**
     * The responsive rules for the items
     * ex: { xxsmall: 2, medium: 4, xlarge: 6 }
     * mobile first like foundation grid
     */
    responsiveRules: {
      type: Object,
      default: function () {}
    },
    /**
     * Set default margins
     */
    withMargins: {
      type: Boolean,
      default: true
    },
    /**
     * to show the arrow for scrolling
     */
    showArrows: {
      type: Boolean,
      default: false
    },

    showScrollBar: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      containerScrollWidth: 0,
      clientWidth: 0,
      isScrollingTicked: false,
      intervalScrollingId: undefined,
      onScrollEventCount: 0,
      hidingNativeScrollbarHeight: 'unset',
      scrollGripperXPosition: 0,
      carouselContainerComputedStyle: {},
      scrollGripperWidth: 0,
      isLeftmost: true,
      isRightmost: false,
      // The start position (pageX) of the mouse when the user triggers a mousedown event on the scrollbar
      mouseDownStartX: 0,
      // The scroll left position of the scroll container when the user triggers a mousedown event on the scrollbar
      scrollLeftStartPosition: 0,
      // the direction to use in autoscroll, can be 1 (right) or -1 (left)
      scrollDirection: null,
      // the animation used for autoscrolling
      autoScroll: new Animation(() => { this.$refs.scrollContainer.scrollLeft += this.step * this.scrollDirection }),
      // Used for detect touch device, because :hover not working to display the scrollbar
      isScrolling: false,
      // For detect scroll ending
      scrollEndTimeout: 0,
      // Is there enough items to allow scrolling
      isScrollableCarousel: false
    }
  },
  computed: {
    // based on responsiveRules prop
    responsiveClasses: function () {
      if (this.responsiveRules) {
        return `xxsmall-${this.responsiveRules.xxsmall}_33
              xsmall-${this.responsiveRules.xsmall}_33
              small-${this.responsiveRules.small}_33
              medium-${this.responsiveRules.medium}_33
              large-${this.responsiveRules.large}_33
              xlarge-${this.responsiveRules.xlarge}_33
              xxlarge-${this.responsiveRules.xxlarge}_33`
      } else {
        return ''
      }
    }
  },
  watch: {
    carouselItems () {
      this.$refs.scrollContainer.scrollLeft = 0
    },
    isLeftmost (newVal) {
      if (newVal) this.stopAutoScrolling()
    },
    isRightmost (newVal) {
      if (newVal) this.stopAutoScrolling()
    }
  },
  mounted () {
    this.updateSizes()
    this.$nextTick(() => {
      this.updateSizes().then(() => {
        this.$emit('ready') // all sizes are good
      })
    })
    window.addEventListener('resize', this.updateSizes)
  },
  updated: function () {
    this.$nextTick(() => {
      this.updateHeightAndWidth()
    })
  },
  beforeDestroy () {
    this.autoScroll.stop()
    window.removeEventListener('resize', this.updateSizes)
  },
  methods: {
    /**
     * horizontally scroll the displayed number of items
     * @param scrollDirection set to -1 to scroll to the left and set to 1 to scroll to the right
     */
    clickArrow (scrollDirection) {
      const windowWidth = window.innerWidth
      const itemContainerScrollable = this.$vnode.elm.getElementsByClassName('scroll-container')[0]
      const itemsWidth = this.$vnode.elm.getElementsByClassName('items-container')[0].firstChild.scrollWidth

      itemContainerScrollable.scrollLeft = itemContainerScrollable.scrollLeft + scrollDirection * (Math.floor(windowWidth / itemsWidth) * itemsWidth)
    },

    /**
     * Compute the scrollContainerHeight and width
     */
    updateHeightAndWidth () {
      this.carouselContainerComputedStyle = window.getComputedStyle(this.$refs.carouselContainer)
      const height = this.$refs.itemsContainerWithOffset.getBoundingClientRect().height
      // Hard stylling to have an non empty value at component creation (0px) with computed value in vuejs => issue on Orange portal with LazyLoadingManager
      this.$refs.carouselContainer.style.height = height + 30/* custom slider container max height */ + 'px'
      this.containerScrollWidth = this.$refs.scrollContainer.scrollWidth
      this.clientWidth = this.$refs.scrollContainer.clientWidth
      this.isScrollableCarousel = this.clientWidth < this.containerScrollWidth
      this.hidingNativeScrollbarHeight = height + 60 + 'px' /* custom slider container height + 60px to hide scrollbar */
    },
    /**
     * Compute the scrollContainerHeight and call the gripper update
     */
    updateSizes () {
      this.updateHeightAndWidth()
      return new Promise((resolve, reject) => {
        this.$nextTick(() => {
          if (this.isScrollableCarousel) {
            this.updateBounds()
            // Needed for IOS environnement
            this.scrollGripperWidth = this.$refs.scrollGripper.getBoundingClientRect().width
            this.updateGripper(this.$refs.scrollContainer)
            resolve()
          }
        })
      })
    },

    /**
     * Compute the scroll gripper position according to the scroll left position of the scroll container content
     * @param  {HTML Element} the scroll container element
     */
    updateGripper (scrollLeft) {
      // Compute the percentage from moving the native scrollbar compared to the global width minus the visible width
      const percent = scrollLeft * 100 / (this.containerScrollWidth - this.clientWidth)
      // To move the gripper
      this.scrollGripperXPosition = (percent * (this.clientWidth - (this.scrollGripperWidth + parseInt(this.carouselContainerComputedStyle.paddingLeft) * 2))) / 100
      this.isScrollingTicked = false
    },

    /**
     * Enable/Disable bounds according to scroll position
     */
    updateBounds () {
      if (this.$refs.scrollContainer) {
        this.isLeftmost = this.$refs.scrollContainer.scrollLeft === 0
        this.isRightmost = this.$refs.scrollContainer.scrollLeft >= this.$refs.scrollContainer.scrollWidth - this.$refs.scrollContainer.clientWidth
        this.$nextTick(() => {
          this.$emit('bound-updated')
        })
      }
    },

    /**
     * Initialize autoscrolling with the good direction
     * @param  {Integer} 1 for the right, -1 for the left
     */
    startAutoScrolling (direction) {
      this.scrollDirection = direction
      this.autoScroll.start()
    },

    /**
     * Finish autoscrolling animation
     */
    stopAutoScrolling () {
      this.autoScroll.stop()
    },

    /**
     * Scroll start event on the scroll container
     * @param  {Event}
     */
    onScrollEvent (event) {
      const scrollLeft = event.target.scrollLeft
      if (!this.isScrollingTicked) {
        window.requestAnimationFrame(this.updateGripper.bind(this, scrollLeft))
      }
      this.isScrollingTicked = true

      this.isScrolling = true
      this.updateBounds()

      this.onScrollEventCount += 1
      let savedCount = this.onScrollEventCount
      if (!this.intervalScrollingId) {
        this.intervalScrollingId = window.setInterval(() => {
          if (savedCount === this.onScrollEventCount) {
            clearInterval(this.intervalScrollingId)
            this.intervalScrollingId = undefined
            this.onScrollEventCount = 0
            savedCount = 0
            this.isScrolling = false
          } else {
            savedCount = this.onScrollEventCount
          }
        }, 300)
      }
    },

    /**
     * Mousedown event on the scroll gripper
     * @param  {Event}
     */
    onMouseDownEventScrollGripper (event) {
      // Save current X position inside the viewport
      this.mouseDownStartX = event.pageX
      // Save current scroll left position of the scroll container element
      this.scrollLeftStartPosition = this.$refs.scrollContainer.scrollLeft
      document.addEventListener('mousemove', this.updateScrollPosition)
      document.addEventListener('mouseup', this.stopScrollPosition)

      // To prevent selecting text when mousedown then mousemove actions outside the gripper area (IE)
      this.setUserSelectStyle('none')
    },

    /**
     * Mousemouve event on the scroll gripper
     * @param  {Event}
     */
    updateScrollPosition (event) {
      this.$refs.scrollContainer.scrollLeft = this.scrollLeftStartPosition +
        (this.$refs.scrollContainer.scrollWidth - this.$refs.scrollContainer.clientWidth) *
        (event.pageX - this.mouseDownStartX) /
        (this.$refs.scrollContainer.clientWidth - this.scrollGripperWidth)
    },

    /**
     * Mouseup event on the scroll gripper
     * @param  {Event}
     */
    stopScrollPosition (event) {
      document.removeEventListener('mousemove', this.updateScrollPosition)
      document.removeEventListener('mouseup', this.stopScrollPosition)

      // To prevent selecting text when mousedown then mousemove outside the gripper area (IE)
      this.setUserSelectStyle('auto')
    },

    /**
     * Set the user select css property to the body element
     * @param  {String} the css value to set
     */
    setUserSelectStyle (cssValue) {
      document.body.style.webkitTouchCallout = cssValue
      document.body.style.webkitUserSelect = cssValue
      document.body.style.MozUserSelect = cssValue
      document.body.style.msUserSelect = cssValue
    },

    /**
     * Scroll left of given quantity without transition
     * @param {integer} the scroll quantity (positive to scroll left, negative to scroll right)
     */
    scroll (value) {
      this.$refs.scrollContainer.scrollLeft += value
    }
  }
}
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
//============================ IMPORTS

@import '../../scss/abstracts/variables';
@import '../../scss/abstracts/mixins';

//============================ STATICS VARS

$light-scroll-gripper-color: #999;
$dark-scroll-gripper-color: #666;

//============================ STATICS CSS

.carousel-container {
  width: 100%;
  position: relative;
  background: transparent;
  box-sizing: border-box;
  @include mediaQueries('padding-left', $main-padding-left);

  :focus {
    outline: 0;
  }

  //Themes ('light' is default one)
  &.dark {
    background: $black;

    .scroll-gripper-container {
      display: block;
      width: 100%;
      transition: opacity 0.3s ease-in-out;
      margin-top: -50px;
      opacity: 0;

      .scroll-gripper {
        @include mediaQueries('height', (xxsmall: 7px, medium: 9px));
        width: 40%;
        background-color: $dark-scroll-gripper-color;
        border-radius: 5px;
      }
    }
  }
  &.dark-img {

    .scroll-gripper-container {
      display: block;
      width: 100%;
      transition: opacity 0.3s ease-in-out;
      margin-top: -50px;
      opacity: 0;

      .scroll-gripper {
        @include mediaQueries('height', (xxsmall: 7px, medium: 9px));
        width: 40%;
        background-color: $dark-scroll-gripper-color;
        border-radius: 5px;
      }
    }
  }

  .scroll-container {
    overflow-x: scroll;
    @include mediaQueries('margin-left', $main-padding-left-scroll, -1);
    // Allow smooth swipe on IOS
    -webkit-overflow-scrolling: touch;
    // hide native scrollbar on IE (for windows phone/MsEdge)
    -ms-overflow-style: none;
    // hide native scrollbar on old firefox
    overflow: -moz-scrollbars-none;
    // hide native scrollbar on most recent firefox
    scrollbar-width: none;
    // hide native scrollbar on Chrome (mobile)
    &::-webkit-scrollbar {
      display: none;
    }

    .items-container {
      white-space: nowrap;
      font-size: 0;

      &.spaced {
        @include mediaQueries('margin-left', $main-padding-left);
      }
    }
  }

  .scroll-gripper-container {
    display: block;
    width: 100%;
    transition: opacity 0.3s ease-in-out;
    opacity: 0;
    margin-top: -50px;

    .scroll-gripper {
      @include mediaQueries('height', (xxsmall: 7px, medium: 9px));
      width: 40%;
      will-change: transform;
      background-color: $light-scroll-gripper-color;
      border-radius: 5px;
    }
  }

  &:hover .scroll-gripper-container,
  &.scrolling .scroll-gripper-container {
    opacity: 1;
  }

  .bound {
    display: block;
    top: 0;
    position: absolute;
    z-index: 3;
    width: 5%;
    height: 100%;

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }

    .arrow {
      border: none;
      cursor: pointer;
      position: absolute;
      height: 2.5rem;
      width: 2.5rem;
      background: rgba(0,0,0,.4);
      display: flex;
      justify-content: center;
      top: 25%;

      .icon-arrow-next, .icon-arrow-previous {
        line-height: 0;
        font-size: 1rem;
        align-self: center;
        color: #fff;
      }

      &.right-arrow {
        right: 0;
      }
    }
  }

  // this media query not working on IE&FF, so default is bound enabled, (disabled for touch devices)
  @media (pointer:coarse) {
    .scroll-container.cover .item:focus .focus-border,
    .scroll-container.banner .item:focus .focus-border {
      display: none;
    }

    .bound {
      display: none;
    }

    // On touch device, the hover stay on the element after being clicked, the scroll bar is still visible even the scrolling is stopped
    // So don't display it using :hover property
    &:hover:not(.scrolling) .scroll-gripper-container {
      opacity: 0;
    }
  }
  @media (pointer:none) {
    .scroll-container.cover .item:focus .focus-border,
    .scroll-container.banner .item:focus .focus-border {
      display: none;
    }

    .bound {
      display: none;
    }

    &:hover:not(.scrolling) .scroll-gripper-container {
      opacity: 0;
    }
  }

  .scroll-container > div {
    padding-top: 10px;
  }

  &:not(.light) {
    .scroll-container > div {
      padding-top: 25px;
    }
  }
}

// hide native scrollbar on most recent firefox
@-moz-document url-prefix() {
  .carousel-container {
    .scroll-container {
      overflow: hidden !important;
    }
  }
}
</style>
